import {
  Card,
  CardHeader,
  Divider,
  CardContent,
  Stack,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Box,
  Typography,
  OutlinedInput,
  CardActions,
  Button,
  InputAdornment,
  IconButton,
} from "@mui/material";
import { LocalizationProvider, DateField } from "@mui/x-date-pickers";
import { AdapterMoment } from "@mui/x-date-pickers/AdapterMoment";
import moment from "moment-timezone";
import React, { useState, useMemo } from "react";
import { useGenerateApiToken } from "../../../api/token/use-token";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import { useSnackbar } from "../../../context/SnackbarContext";

export function ApiTokenCard() {
  const [apiToken, setApiToken] = useState<string>("");
  const [revealed, setRevealed] = useState<boolean>(false);
  const [expirationOption, setExpirationOption] = useState<string>("90");

  const defaultCustomDate = useMemo(
    () => new Date(Date.now() + 120 * 24 * 60 * 60 * 1000),
    []
  );
  const [customExpirationDate, setCustomExpirationDate] = useState<Date | null>(
    defaultCustomDate
  );

  const generateToken = useGenerateApiToken();
  const { showSnackbar } = useSnackbar();

  const handleGenerateToken = () => {
    let deltaPayload: { days: number };

    if (expirationOption === "custom") {
      if (!customExpirationDate) {
        console.error("Please select a custom expiration date");
        return;
      }
      const now = new Date();
      const diffMs = customExpirationDate.getTime() - now.getTime();
      const diffDays = Math.ceil(diffMs / (1000 * 3600 * 24));
      if (diffDays <= 0) {
        console.error("Custom expiration date must be in the future");
        return;
      }
      deltaPayload = { days: diffDays };
    } else {
      deltaPayload = { days: Number(expirationOption) };
    }

    generateToken.mutate(
      { delta: deltaPayload },
      {
        onSuccess: (data) => {
          setApiToken(data.access_token);
          setRevealed(false);
        },
        onError: (error) => {
          console.error("Failed to generate token", error);
        },
      }
    );
  };

  const handleCopyToken = async () => {
    if (!apiToken) return;
    try {
      await navigator.clipboard.writeText(apiToken);
      showSnackbar("Token copied to clipboard!", "success");
    } catch (error) {
      console.error("Failed to copy token", error);
      showSnackbar("Failed to copy token", "error");
    }
  };

  const handleToggleReveal = () => {
    setRevealed((prev) => !prev);
  };

  const displayToken =
    revealed || !apiToken
      ? apiToken
      : `${apiToken.slice(0, 4)}...${apiToken.slice(-4)}`;

  return (
    <Card>
      <CardHeader
        title="API Token"
        subheader="Generate and copy your API token. Customize the expiration using preset options or select a custom date."
      />
      <Divider />
      <CardContent>
        <Stack spacing={2}>
          <FormControl fullWidth>
            <InputLabel id="expiration-select-label">Expiration</InputLabel>
            <Select
              labelId="expiration-select-label"
              value={expirationOption}
              label="Expiration"
              onChange={(e) => setExpirationOption(e.target.value)}
            >
              <MenuItem value="7">7 days</MenuItem>
              <MenuItem value="30">30 days</MenuItem>
              <MenuItem value="60">60 days</MenuItem>
              <MenuItem value="90">90 days</MenuItem>
              <MenuItem value="custom">Custom</MenuItem>
            </Select>
          </FormControl>
          {expirationOption === "custom" && (
            <Box sx={{ border: "1px solid #ccc", borderRadius: 1, p: 1 }}>
              <Typography variant="body2" sx={{ mb: 1 }}>
                Select Custom Expiration Date:
              </Typography>
              <LocalizationProvider dateAdapter={AdapterMoment}>
                <DateField
                  label="Expiration Date"
                  defaultValue={moment(customExpirationDate)}
                  onChange={(newValue) =>
                    setCustomExpirationDate(newValue ? newValue.toDate() : null)
                  }
                  fullWidth
                />
              </LocalizationProvider>
            </Box>
          )}
          <FormControl fullWidth>
            <InputLabel htmlFor="api-token">API Token</InputLabel>
            <OutlinedInput
              id="api-token"
              value={displayToken}
              label="API Token"
              readOnly
              onClick={handleCopyToken}
              sx={{ cursor: "pointer" }}
              endAdornment={
                <InputAdornment position="end">
                  <IconButton onClick={handleCopyToken} edge="end">
                    <ContentCopyIcon />
                  </IconButton>
                </InputAdornment>
              }
            />
          </FormControl>
        </Stack>
      </CardContent>
      <Divider />
      <CardActions sx={{ justifyContent: "flex-end" }}>
        <Button
          variant="contained"
          onClick={handleGenerateToken}
          disabled={generateToken.isPending}
        >
          {generateToken.isPending ? "Generating..." : "Generate"}
        </Button>
        <Button
          variant="text"
          onClick={handleToggleReveal}
          disabled={!apiToken}
        >
          {revealed ? "Hide" : "Show"}
        </Button>
      </CardActions>
    </Card>
  );
}
